"use client"

import { clx } from "@modules/common/components/ui"
import { useEffect, useState } from "react"

// Warehouse batch rules (store owner): orders enter the daily shipment batch
// at 2:30pm America/Los_Angeles, Mon-Fri. Mon-Thu the batch physically ships
// the same day. Friday's batch is picked and gets tracking numbers, but
// nothing leaves the dock until Monday. Sat/Sun nothing runs.
const CUTOFF_MINUTES = 14 * 60 + 30 // 2:30pm PT

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

// Wall-clock time in the warehouse's timezone, regardless of where the
// server rendered or the browser is. formatToParts with an explicit
// timeZone is the only reliable way to get this without a date library.
function getPacificNow(): { day: number; secondsIntoDay: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    weekday: "short",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hourCycle: "h23",
  }).formatToParts(new Date())

  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? ""

  return {
    day: WEEKDAY_INDEX[get("weekday")] ?? 0,
    secondsIntoDay:
      Number(get("hour")) * 3600 +
      Number(get("minute")) * 60 +
      Number(get("second")),
  }
}

function getMessage(): string {
  const { day, secondsIntoDay } = getPacificNow()
  const remaining = CUTOFF_MINUTES * 60 - secondsIntoDay
  const beforeCutoff = remaining > 0

  const totalMinutes = Math.max(1, Math.ceil(remaining / 60))
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  const window = h > 0 ? `${h}h ${m}m` : `${m}m`

  // Mon-Thu before cutoff: makes today's truck
  if (day >= 1 && day <= 4 && beforeCutoff) {
    return `Order within ${window} to have your order ship today`
  }
  // Thu after cutoff: enters Friday's batch — tracking Friday, leaves Monday
  if (day === 4) {
    return "Order now — processed tomorrow, ships Monday"
  }
  // Mon-Wed after cutoff: makes tomorrow's truck
  if (day >= 1 && day <= 3) {
    return "Order now and your order ships tomorrow"
  }
  // Friday before cutoff: processed today, leaves Monday
  if (day === 5 && beforeCutoff) {
    return `Order within ${window} — we'll process it today and it ships first thing Monday`
  }
  // Friday after cutoff, Saturday, Sunday
  return "Order now and your order ships Monday"
}

type ShipCutoffBannerProps = {
  compact?: boolean
  className?: string
}

export default function ShipCutoffBanner({
  compact,
  className,
}: ShipCutoffBannerProps) {
  const [message, setMessage] = useState<string | null>(null)

  // Client-only: computed after mount so server HTML and first client render
  // agree (no hydration mismatch), then ticked so the countdown stays live.
  useEffect(() => {
    setMessage(getMessage())
    const interval = setInterval(() => setMessage(getMessage()), 1000)
    return () => clearInterval(interval)
  }, [])

  if (!message) {
    return null
  }

  return (
    <div
      className={clx(
        "flex items-center gap-x-2 rounded-md bg-neutral-900 text-amber-400",
        compact ? "px-3 py-1.5" : "px-3.5 py-2",
        className
      )}
      data-testid="ship-cutoff-banner"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={clx("shrink-0", compact ? "h-3.5 w-3.5" : "h-4 w-4")}
      >
        <path d="M1 5h13v11H1z" />
        <path d="M14 9h4l4 4v3h-8" />
        <circle cx="6" cy="17.5" r="1.8" />
        <circle cx="18" cy="17.5" r="1.8" />
      </svg>
      <span
        className={clx("font-medium", compact ? "text-xs" : "text-sm")}
        role="status"
      >
        {message}
      </span>
    </div>
  )
}
